window.MCOTheme = window.MCOTheme || {};

(() => {
  const sliderSelector = '[data-mco-hero-slider]';

  const initHeroSlider = (slider) => {
    if (!slider || slider.mcoHeroSwiper || typeof window.Swiper === 'undefined') return;

    const slideCount = slider.querySelectorAll('.swiper-wrapper > .swiper-slide').length;
    const canRotate = slideCount > 1;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const autoplayEnabled = slider.dataset.autoplay === 'true' && canRotate && !reduceMotion;
    const delay = Number.parseInt(slider.dataset.delay, 10) || 5000;

    const options = {
      allowTouchMove: canRotate,
      autoplay: autoplayEnabled
        ? {
          delay,
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
        }
        : false,
      direction: 'horizontal',
      effect: 'slide',
      loop: slider.dataset.loop === 'true' && canRotate,
      observer: true,
      observeParents: true,
      resizeObserver: true,
      speed: reduceMotion ? 0 : 700,
      updateOnWindowResize: true,
      watchOverflow: true,
    };

    const hero = slider.closest('[data-mco-hero]');
    const pagination = hero?.querySelector('.mco-hero__pagination');
    if (pagination) {
      options.pagination = {
        el: pagination,
        clickable: true,
      };
    }

    slider.mcoHeroSwiper = new window.Swiper(slider, options);
  };

  const initHeroSections = (scope = document) => {
    if (typeof window.Swiper === 'undefined') return false;

    if (scope.matches?.(sliderSelector)) initHeroSlider(scope);
    scope.querySelectorAll?.(sliderSelector).forEach(initHeroSlider);
    return true;
  };

  const bootHeroSections = () => {
    if (!initHeroSections()) {
      window.addEventListener('load', () => initHeroSections(), { once: true });
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootHeroSections, { once: true });
  } else {
    bootHeroSections();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initHeroSections(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll?.(sliderSelector).forEach((slider) => {
      if (slider.mcoHeroSwiper) {
        slider.mcoHeroSwiper.destroy(true, true);
        slider.mcoHeroSwiper = null;
      }
    });
  });

  document.addEventListener('shopify:block:select', (event) => {
    const slide = event.target.closest?.('[data-mco-hero-block]');
    const slider = slide?.closest(sliderSelector);
    if (!slide || !slider?.mcoHeroSwiper) return;

    const slides = Array.from(slider.querySelectorAll('[data-mco-hero-block]'));
    const index = slides.indexOf(slide);
    if (index < 0) return;

    if (typeof slider.mcoHeroSwiper.slideToLoop === 'function') {
      slider.mcoHeroSwiper.slideToLoop(index, 0);
    } else {
      slider.mcoHeroSwiper.slideTo(index, 0);
    }
    slider.mcoHeroSwiper.autoplay?.stop();
  });
})();

(() => {
  const mediaSelector = '[data-mco-product-media]';
  const slideDuration = 650;
  const slideDelay = 1200;
  const initialDelay = 300;

  const initProductMedia = (media) => {
    if (!media || media.mcoMediaCleanup) return;

    const images = Array.from(
      media.querySelectorAll('[data-mco-product-image], .mco-product-slide-image')
    );
    if (images.length < 2) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    const interactionTarget = media.closest('.mco-product-card, .product-card-wrapper') || media;
    const track = document.createElement('span');
    const firstImage = images[0];
    const loopClone = firstImage.cloneNode(true);
    let activeIndex = 0;
    let rotationTimer = null;
    let startTimer = null;
    let resetTimer = null;

    track.className = 'mco-product-image-track';
    track.setAttribute('aria-hidden', 'true');
    firstImage.before(track);

    images.forEach((image, imageIndex) => {
      image.classList.toggle('mco-product-card__image--active', imageIndex === 0);
      image.setAttribute('aria-hidden', 'true');
      track.append(image);
    });

    loopClone.classList.add('mco-product-image-track__clone');
    loopClone.classList.remove('mco-product-card__image--active');
    loopClone.removeAttribute('id');
    loopClone.setAttribute('alt', '');
    loopClone.setAttribute('aria-hidden', 'true');
    track.append(loopClone);

    media.dataset.mcoProductSliderReady = '';
    media.style.setProperty('--mco-product-slide-duration', `${slideDuration}ms`);

    const showImage = (index, animate = true) => {
      activeIndex = index;
      track.classList.toggle('mco-product-image-track--no-transition', !animate);
      track.style.setProperty('--mco-product-slide-offset', `${activeIndex * -100}%`);
      images.forEach((image, imageIndex) => {
        image.classList.toggle('mco-product-card__image--active', imageIndex === activeIndex);
      });
    };

    const resetLoopPosition = () => {
      showImage(0, false);
      void track.offsetWidth;
      window.requestAnimationFrame(() => {
        track.classList.remove('mco-product-image-track--no-transition');
      });
    };

    const advanceSlide = () => {
      const nextIndex = activeIndex + 1;
      showImage(nextIndex);

      if (nextIndex === images.length) {
        resetTimer = window.setTimeout(resetLoopPosition, slideDuration + 30);
      }
    };

    const stopRotation = () => {
      if (startTimer) window.clearTimeout(startTimer);
      if (rotationTimer) window.clearInterval(rotationTimer);
      if (resetTimer) window.clearTimeout(resetTimer);
      startTimer = null;
      rotationTimer = null;
      resetTimer = null;
      media.classList.remove('mco-product-media--active');

      if (activeIndex >= images.length) {
        resetLoopPosition();
      } else {
        showImage(0);
      }
    };

    const startRotation = () => {
      if (reduceMotion || !canHover || rotationTimer || startTimer) return;
      media.classList.add('mco-product-media--active');
      startTimer = window.setTimeout(() => {
        startTimer = null;
        advanceSlide();
        rotationTimer = window.setInterval(advanceSlide, slideDelay);
      }, initialDelay);
    };

    const handleFocusOut = (event) => {
      if (!interactionTarget.contains(event.relatedTarget)) stopRotation();
    };

    interactionTarget.addEventListener('pointerenter', startRotation);
    interactionTarget.addEventListener('pointerleave', stopRotation);
    interactionTarget.addEventListener('focusin', startRotation);
    interactionTarget.addEventListener('focusout', handleFocusOut);

    media.mcoMediaCleanup = () => {
      stopRotation();
      interactionTarget.removeEventListener('pointerenter', startRotation);
      interactionTarget.removeEventListener('pointerleave', stopRotation);
      interactionTarget.removeEventListener('focusin', startRotation);
      interactionTarget.removeEventListener('focusout', handleFocusOut);
      images.forEach((image) => {
        image.removeAttribute('aria-hidden');
        media.insertBefore(image, track);
      });
      track.remove();
      delete media.dataset.mcoProductSliderReady;
      media.style.removeProperty('--mco-product-slide-duration');
      media.mcoMediaCleanup = null;
    };
  };

  const initProductCards = (scope = document) => {
    if (scope.matches?.(mediaSelector)) initProductMedia(scope);
    scope.querySelectorAll?.(mediaSelector).forEach(initProductMedia);
  };

  const observeProductCards = () => {
    initProductCards();
    if (!document.body || document.body.mcoProductCardObserver) return;

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) initProductCards(node);
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
    document.body.mcoProductCardObserver = observer;
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeProductCards, { once: true });
  } else {
    observeProductCards();
  }

  document.addEventListener('shopify:section:load', (event) => initProductCards(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll?.(mediaSelector).forEach((media) => media.mcoMediaCleanup?.());
  });
})();

/* ============================================================
   MCO VIDEO SHOWCASE SECTION
   ============================================================ */

(() => {
  const sectionSelector = '[data-mco-video-showcase]';
  const cardSelector = '[data-mco-video-card]';

  const stopVideoCard = (card) => {
    if (!card) return;

    const player = card.querySelector('[data-mco-video-player]');
    const video = player?.querySelector('video');
    video?.pause();

    if (player) {
      player.replaceChildren();
      player.hidden = true;
    }

    card.classList.remove('is-playing');
  };

  const playVideoCard = (card) => {
    const template = card?.querySelector('[data-mco-video-template]');
    const player = card?.querySelector('[data-mco-video-player]');
    if (!template || !player) return;

    document.querySelectorAll(`${cardSelector}.is-playing`).forEach((activeCard) => {
      if (activeCard !== card) stopVideoCard(activeCard);
    });

    player.replaceChildren(template.content.cloneNode(true));
    player.hidden = false;
    card.classList.add('is-playing');

    const video = player.querySelector('video');
    if (video) {
      const playPromise = video.play();
      playPromise?.catch?.(() => { });
    }
  };

  const initVideoShowcase = (section) => {
    if (!section || section.mcoVideoShowcaseCleanup) return;

    const cleanupCallbacks = [];
    section.querySelectorAll('[data-mco-video-trigger]').forEach((button) => {
      const card = button.closest(cardSelector);
      const handleClick = () => playVideoCard(card);
      button.addEventListener('click', handleClick);
      cleanupCallbacks.push(() => button.removeEventListener('click', handleClick));
    });

    section.mcoVideoShowcaseCleanup = () => {
      section.querySelectorAll(cardSelector).forEach(stopVideoCard);
      cleanupCallbacks.forEach((cleanup) => cleanup());
      section.mcoVideoShowcaseCleanup = null;
    };
  };

  const initVideoShowcases = (scope = document) => {
    if (scope.matches?.(sectionSelector)) initVideoShowcase(scope);
    scope.querySelectorAll?.(sectionSelector).forEach(initVideoShowcase);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initVideoShowcases(), { once: true });
  } else {
    initVideoShowcases();
  }

  document.addEventListener('shopify:section:load', (event) => initVideoShowcases(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target.matches?.(sectionSelector)) event.target.mcoVideoShowcaseCleanup?.();
    event.target.querySelectorAll?.(sectionSelector).forEach((section) => section.mcoVideoShowcaseCleanup?.());
  });
})();

/* END MCO VIDEO SHOWCASE SECTION */

/* ============================================================
   MCO PERSONALISED GIFTING CAROUSEL
   ============================================================ */

(() => {
  const carouselSelector = '[data-mco-personalised-carousel]';

  const initPersonalisedCarousel = (carousel) => {
    if (!carousel || carousel.mcoPersonalisedSwiper || typeof window.Swiper === 'undefined') return;

    const galleryCount = Number.parseInt(carousel.dataset.galleryCount, 10) || 0;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const autoplayEnabled = carousel.dataset.autoplay === 'true' && galleryCount > 1 && !reduceMotion;
    const delay = Number.parseInt(carousel.dataset.delay, 10) || 4000;

    carousel.mcoPersonalisedSwiper = new window.Swiper(carousel, {
      allowTouchMove: galleryCount > 1,
      autoplay: autoplayEnabled
        ? {
          delay,
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
        }
        : false,
      breakpoints: {
        0: { slidesPerView: 1.08, spaceBetween: 16 },
        750: { slidesPerView: 2, spaceBetween: 20 },
        990: { slidesPerView: 3, spaceBetween: 25 },
      },
      loop: autoplayEnabled,
      observer: true,
      observeParents: true,
      resizeObserver: true,
      speed: reduceMotion ? 0 : 700,
      updateOnWindowResize: true,
      watchOverflow: true,
    });
  };

  const initPersonalisedCarousels = (scope = document) => {
    if (typeof window.Swiper === 'undefined') return false;
    if (scope.matches?.(carouselSelector)) initPersonalisedCarousel(scope);
    scope.querySelectorAll?.(carouselSelector).forEach(initPersonalisedCarousel);
    return true;
  };

  const bootPersonalisedCarousels = () => {
    if (!initPersonalisedCarousels()) {
      window.addEventListener('load', () => initPersonalisedCarousels(), { once: true });
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootPersonalisedCarousels, { once: true });
  } else {
    bootPersonalisedCarousels();
  }

  document.addEventListener('shopify:section:load', (event) => initPersonalisedCarousels(event.target));

  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll?.(carouselSelector).forEach((carousel) => {
      carousel.mcoPersonalisedSwiper?.destroy(true, true);
      carousel.mcoPersonalisedSwiper = null;
    });
  });

  document.addEventListener('shopify:block:select', (event) => {
    const slide = event.target.closest?.('.mco-personalised__slide:not([aria-hidden="true"])');
    const carousel = slide?.closest(carouselSelector);
    if (!slide || !carousel?.mcoPersonalisedSwiper) return;

    const originals = Array.from(carousel.querySelectorAll('.mco-personalised__slide:not([aria-hidden="true"])'));
    const index = originals.indexOf(slide);
    if (index < 0) return;

    carousel.mcoPersonalisedSwiper.slideToLoop?.(index, 0);
    carousel.mcoPersonalisedSwiper.autoplay?.stop();
  });
})();

/* END MCO PERSONALISED GIFTING CAROUSEL */

/* ============================================================
   MCO PRODUCT EXPERIENCES
   Standard PDP, mixed box, dry-fruit builder, hamper and bulk.
   Shopify variants remain the price and availability authority.
   ============================================================ */

(() => {
  const rootSelector = '[data-mco-product]';

  const normalize = (value = '') => value
    .toString()
    .trim()
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

  const choiceKey = (values) => values.map(normalize).filter(Boolean).sort().join('|');

  const formatMoney = (cents, format) => {
    const amount = (Number(cents || 0) / 100).toFixed(2);
    const amountNoDecimals = Math.round(Number(cents || 0) / 100).toString();
    const amountWithComma = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const amountNoDecimalsWithComma = amountNoDecimals.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return (format || '₹{{amount}}')
      .replace(/{{\s*amount_no_decimals_with_comma_separator\s*}}/g, amountNoDecimalsWithComma)
      .replace(/{{\s*amount_no_decimals\s*}}/g, amountNoDecimalsWithComma)
      .replace(/{{\s*amount_with_comma_separator\s*}}/g, amountWithComma)
      .replace(/{{\s*amount\s*}}/g, amountWithComma);
  };

  const initProduct = (root) => {
    if (!root || root.dataset.mcoReady === 'true') return;
    root.dataset.mcoReady = 'true';

    const variantsNode = root.querySelector('[data-mco-variants]');
    const form = root.querySelector('.mco-pdp__form');
    if (!variantsNode || !form) return;

    let variants = [];
    try { variants = JSON.parse(variantsNode.textContent); } catch (error) { return; }

    const mode = root.dataset.mode;
    const moneyFormat = root.dataset.moneyFormat;
    const idInput = form.querySelector('[data-mco-variant-id]');
    const price = root.querySelector('[data-mco-price]');
    const compare = root.querySelector('[data-mco-compare]');
    const sale = root.querySelector('[data-mco-sale]');
    const addButton = root.querySelector('[data-mco-add]');
    const addLabel = root.querySelector('[data-mco-add-label]');
    const quantity = root.querySelector('[data-mco-quantity]');
    const bulkSummary = root.querySelector('[data-bulk-summary]');

    const setAddState = (enabled, label) => {
      if (!addButton) return;
      addButton.disabled = !enabled;
      addButton.setAttribute('aria-disabled', enabled ? 'false' : 'true');
      if (addLabel && label) addLabel.textContent = label;
    };

    const showMedia = (mediaId) => {
      if (!mediaId) return;
      const nativeGallery = root.querySelector('media-gallery');
      if (nativeGallery) {
        const nativeMediaId = `${root.dataset.sectionId}-${mediaId}`;
        if (typeof nativeGallery.setActiveMedia === 'function') {
          nativeGallery.setActiveMedia(nativeMediaId, true);
        }
        return;
      }
      root.querySelectorAll('[data-media-id]').forEach((media) => {
        const active = String(media.dataset.mediaId) === String(mediaId);
        media.hidden = !active;
        media.classList.toggle('is-active', active);
        if (!active) media.querySelector('video')?.pause();
      });
      root.querySelectorAll('[data-media-target]').forEach((thumb) => {
        thumb.classList.toggle('is-active', String(thumb.dataset.mediaTarget) === String(mediaId));
      });
    };

    const updateVariant = (variant, options = {}) => {
      const { forceBlocked = false, blockedLabel = 'Pricing unavailable' } = options;
      if (!variant) {
        idInput.value = '';
        setAddState(false, blockedLabel);
        return;
      }

      idInput.value = variant.id;
      if (price) price.innerHTML = formatMoney(variant.price, moneyFormat);
      const onSale = Number(variant.compare_at_price) > Number(variant.price);
      if (compare) {
        compare.hidden = !onSale;
        compare.innerHTML = onSale ? formatMoney(variant.compare_at_price, moneyFormat) : '';
      }
      if (sale) sale.hidden = !onSale;
      showMedia(variant.featured_media?.id || variant.featured_media_id);

      const purchasable = variant.available && Number(variant.price) > 0 && !forceBlocked;
      const label = forceBlocked
        ? blockedLabel
        : !variant.available
          ? 'Sold out'
          : Number(variant.price) <= 0
            ? 'Pricing unavailable'
            : 'Add to cart';
      setAddState(purchasable, label);

      if (window.history?.replaceState && mode !== 'dry_fruit') {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }
    };

    root.querySelectorAll('[data-media-target]').forEach((thumb) => {
      thumb.addEventListener('click', () => showMedia(thumb.dataset.mediaTarget));
    });

    root.querySelectorAll('[data-mco-option]').forEach((input) => {
      input.addEventListener('change', () => {
        root.querySelectorAll('[data-mco-option]').forEach((optionInput) => {
          optionInput.closest('.mco-pdp__option-pill')?.classList.toggle('is-selected', optionInput.checked);
        });
        const selected = Array.from(root.querySelectorAll('[data-mco-option]:checked'))
          .sort((a, b) => Number(a.dataset.mcoOption) - Number(b.dataset.mcoOption))
          .map((item) => item.value);
        const variant = variants.find((item) => item.options.every((value, index) => value === selected[index]));
        const bulkBlocked = mode === 'bulk' && root.dataset.bulkPurchaseEnabled !== 'true';
        updateVariant(variant, bulkBlocked ? { forceBlocked: true, blockedLabel: 'Bulk pricing unavailable' } : {});
      });
    });

    root.querySelectorAll('[data-mco-direct-variant]').forEach((input) => {
      input.addEventListener('change', () => {
        root.querySelectorAll('.mco-pdp__choice-list--variants .mco-pdp__choice-card').forEach((card) => card.classList.remove('is-selected'));
        input.closest('.mco-pdp__choice-card')?.classList.add('is-selected');
        const variant = variants.find((item) => String(item.id) === input.value);
        const property = root.querySelector('[data-mco-combination-property]');
        if (property && variant) property.value = variant.title;
        updateVariant(variant);
      });
    });

    if (mode === 'dry_fruit') {
      const required = Number(root.dataset.requiredChoices) || 4;
      const cards = Array.from(root.querySelectorAll('[data-builder-choice]'));
      const status = root.querySelector('[data-builder-status]');
      const property = root.querySelector('[data-builder-property]');

      const selectedCards = () => cards.filter((card) => card.classList.contains('is-selected'));
      const selectedValues = () => selectedCards().map((card) => card.dataset.choice);

      const variantBuilderKey = (variant) => {
        const source = variant.title === 'Default Title' ? '' : variant.title;
        return choiceKey(source.split(/\s*(?:\/|\+|,|\||•)\s*/));
      };

      const refreshBuilder = () => {
        const selections = selectedValues();
        cards.forEach((card) => {
          const isSelected = card.classList.contains('is-selected');
          card.setAttribute('aria-checked', isSelected ? 'true' : 'false');
        });

        if (property) property.value = selections.join(', ');
        if (status) status.textContent = `${selections.length} of ${required} selected`;

        if (selections.length !== required) {
          idInput.value = '';
          const diff = required - selections.length;
          setAddState(false, diff > 0 ? `CHOOSE ${diff} MORE` : `REMOVE ${Math.abs(diff)}`);
          return;
        }

        const wanted = choiceKey(selections);
        // 1. Try to find a combination-specific variant (e.g. "Kaju / Badam / Pista / Kishmish")
        let variant = variants.find((item) => variantBuilderKey(item) === wanted);

        // 2. If no combination variant exists, fall back to base product variant (fixed box price)
        if (!variant && variants.length > 0) {
          variant = variants.find((item) => item.available && Number(item.price) > 0) || variants[0];
        }

        if (variant) {
          updateVariant(variant);
          if (status) status.textContent = `Selected: ${selections.join(', ')}`;
        } else {
          updateVariant(null, { forceBlocked: true, blockedLabel: 'Pricing unavailable' });
          if (status) status.textContent = 'Selection saved, but no priced variant exists.';
        }
      };

      cards.forEach((card) => {
        card.addEventListener('click', (event) => {
          event.preventDefault();
          const isSelected = card.classList.contains('is-selected');
          const currentCount = selectedCards().length;

          if (isSelected) {
            card.classList.remove('is-selected');
          } else {
            if (currentCount >= required) {
              if (status) status.textContent = `You can only select ${required} items. Deselect one first.`;
              return;
            }
            card.classList.add('is-selected');
          }
          refreshBuilder();
        });
      });

      refreshBuilder();
    }

    const updateWhatsAppEnquiry = () => {
      const whatsappBtn = root.querySelector('[data-mco-whatsapp-button]');
      if (!whatsappBtn) return;

      const phone = (whatsappBtn.dataset.phone || '918439843948').replace(/[^0-9]/g, '');
      const productTitle = whatsappBtn.dataset.productTitle || root.querySelector('.mco-pdp__title')?.textContent?.trim() || 'MCO Custom Box';

      let variantTitle = '';
      const checkedOption = root.querySelector('[data-mco-direct-variant]:checked');
      if (checkedOption) {
        variantTitle = checkedOption.closest('.mco-pdp__choice-card')?.querySelector('.mco-pdp__choice-name')?.textContent?.trim() || '';
      } else {
        const optionPills = Array.from(root.querySelectorAll('.mco-pdp__option-pill.is-selected span')).map((s) => s.textContent.trim());
        if (optionPills.length > 0) variantTitle = optionPills.join(' / ');
      }

      const count = quantity ? Math.max(Number(quantity.min || 1), Number(quantity.value || 1)) : 100;
      const lotSize = Number(root.dataset.bulkLotSize) || 100;
      const totalBoxes = root.dataset.bulkMode === 'lots' ? count * lotSize : count;

      const recipient = root.querySelector('input[name="properties[Recipient or brand name]"]')?.value?.trim() || '';
      const occasion = root.querySelector('input[name="properties[Occasion]"]')?.value?.trim() || '';
      const message = root.querySelector('textarea[name="properties[Gift message]"]')?.value?.trim() || '';

      let text = `Hello MCO Foods! I would like to enquire about bulk / corporate order:\n\n• Product: ${productTitle}`;
      if (variantTitle) text += `\n• Flavour / Option: ${variantTitle}`;
      text += `\n• Quantity: ${totalBoxes} boxes`;
      if (recipient) text += `\n• Brand / Company Name: ${recipient}`;
      if (occasion) text += `\n• Occasion: ${occasion}`;
      if (message) text += `\n• Customization / Notes: ${message}`;
      text += `\n\nPlease share the quote and dispatch timeline.`;

      whatsappBtn.href = `https://wa.me/${phone}?text=${encodeURIComponent(text)}`;
    };

    const updateBulkTotal = () => {
      if (!quantity || mode !== 'bulk') return;
      const entered = Math.max(Number(quantity.min || 1), Number(quantity.value || 1));
      const lotSize = Number(root.dataset.bulkLotSize) || 100;
      const totalBoxes = root.dataset.bulkMode === 'lots' ? entered * lotSize : entered;
      if (bulkSummary) bulkSummary.textContent = `${totalBoxes} boxes in this order`;
      updateWhatsAppEnquiry();
    };

    root.querySelector('[data-quantity-minus]')?.addEventListener('click', () => {
      const min = Number(quantity.min || 1);
      quantity.value = Math.max(min, Number(quantity.value || min) - Number(quantity.step || 1));
      updateBulkTotal();
    });
    root.querySelector('[data-quantity-plus]')?.addEventListener('click', () => {
      quantity.value = Number(quantity.value || quantity.min || 1) + Number(quantity.step || 1);
      updateBulkTotal();
    });
    quantity?.addEventListener('change', () => {
      quantity.value = Math.max(Number(quantity.min || 1), Number(quantity.value || 1));
      updateBulkTotal();
    });

    root.querySelectorAll('.mco-pdp__personalization-fields input, .mco-pdp__personalization-fields textarea').forEach((field) => {
      field.addEventListener('input', updateWhatsAppEnquiry);
    });

    updateBulkTotal();
    updateWhatsAppEnquiry();
  };

  const initAll = (scope = document) => {
    if (scope.matches?.(rootSelector)) initProduct(scope);
    scope.querySelectorAll?.(rootSelector).forEach(initProduct);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  else initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();

/* END MCO PRODUCT EXPERIENCES */

/* ============================================================
   MCO STAGGERED REVIEWS CAROUSEL
   ============================================================ */

(() => {
  const sectionSelector = '[data-mco-reviews]';

  const initReviews = (section) => {
    if (!section || section.dataset.carouselReady === 'true') return;

    const stage = section.querySelector('[data-mco-reviews-stage]');
    const cardTrack = section.querySelector('[data-mco-reviews-cards]');
    const sourceCards = cardTrack
      ? Array.from(cardTrack.querySelectorAll('[data-mco-review-card]:not([data-review-clone])'))
      : [];
    const dots = Array.from(section.querySelectorAll('[data-review-dot]'));
    const previousButton = section.querySelector('[data-mco-reviews-prev]');
    const nextButton = section.querySelector('[data-mco-reviews-next]');
    const sourceTotal = sourceCards.length;

    if (!stage || !cardTrack || !sourceTotal || !previousButton || !nextButton) return;
    section.dataset.carouselReady = 'true';

    const minimumCards = 10;
    let cloneIndex = 0;
    while (cardTrack.children.length < minimumCards) {
      const clone = sourceCards[cloneIndex % sourceTotal].cloneNode(true);
      clone.dataset.reviewClone = 'true';
      clone.dataset.reviewIndex = String(cloneIndex % sourceTotal);
      clone.setAttribute('aria-hidden', 'true');
      Array.from(clone.attributes).forEach((attribute) => {
        if (attribute.name.indexOf('data-shopify') === 0) clone.removeAttribute(attribute.name);
      });
      cardTrack.appendChild(clone);
      cloneIndex += 1;
    }

    const cards = Array.from(cardTrack.querySelectorAll('[data-mco-review-card]'));
    const visualTotal = cards.length;
    let active = Math.min(3, visualTotal - 1);
    let touchStartX = 0;
    let resizeTimer;

    const relativePosition = (index) => {
      let position = index - active;
      const halfway = visualTotal / 2;
      if (position > halfway) position -= visualTotal;
      if (position < -halfway) position += visualTotal;
      return position;
    };

    const render = () => {
      const mobile = window.innerWidth < 640;
      const cardSize = mobile ? 240 : 330;
      const centerOffset = mobile ? -20 : -65;
      cards.forEach((card, index) => {
        const position = relativePosition(index);
        const distance = Math.abs(position);
        const alternatingCard = distance % 2 !== 0;
        const isCenter = position === 0;
        const translateX = (cardSize / 1.5) * position;
        const translateY = isCenter ? centerOffset : alternatingCard ? 15 : -15;
        const rotation = isCenter ? 0 : alternatingCard ? 2.5 : -2.5;

        card.style.width = `${cardSize}px`;
        card.style.height = `${cardSize}px`;
        card.style.zIndex = String(isCenter ? 100 : 50 - distance);
        card.style.transform = `translate(-50%, -50%) translateX(${translateX}px) translateY(${translateY}px) rotate(${rotation}deg)`;
        card.classList.toggle('is-center', isCenter);
        card.setAttribute('aria-hidden', isCenter ? 'false' : 'true');
      });

      dots.forEach((dot, index) => {
        const selected = index === active;
        dot.classList.toggle('is-active', selected);
        dot.setAttribute('aria-current', selected ? 'true' : 'false');
      });

      section.classList.add('is-ready');
    };

    const move = (steps) => {
      if (!steps) return;
      active = (active + steps + visualTotal) % visualTotal;
      window.requestAnimationFrame(render);
    };

    previousButton.addEventListener('click', () => move(-1));
    nextButton.addEventListener('click', () => move(1));

    cards.forEach((card, index) => {
      card.addEventListener('click', () => {
        const position = relativePosition(index);
        if (position !== 0) move(position);
      });
    });

    dots.forEach((dot, desiredPosition) => {
      dot.addEventListener('click', () => {
        move(desiredPosition - active);
      });
    });

    stage.addEventListener('touchstart', (event) => {
      touchStartX = event.touches[0].clientX;
    }, { passive: true });

    stage.addEventListener('touchend', (event) => {
      const distance = event.changedTouches[0].clientX - touchStartX;
      if (Math.abs(distance) > 50) move(distance < 0 ? 1 : -1);
    }, { passive: true });

    section.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') move(-1);
      if (event.key === 'ArrowRight') move(1);
    });

    const handleResize = () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(render, 100);
    };
    window.addEventListener('resize', handleResize);

    section.mcoReviewsCleanup = () => {
      window.removeEventListener('resize', handleResize);
      delete section.dataset.carouselReady;
      section.mcoReviewsCleanup = null;
    };

    render();
  };

  const initAllReviews = (scope = document) => {
    if (scope.matches?.(sectionSelector)) initReviews(scope);
    scope.querySelectorAll?.(sectionSelector).forEach(initReviews);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAllReviews(), { once: true });
  } else {
    initAllReviews();
  }

  document.addEventListener('shopify:section:load', (event) => initAllReviews(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll?.(sectionSelector).forEach((section) => section.mcoReviewsCleanup?.());
  });
})();

/* END MCO STAGGERED REVIEWS CAROUSEL */
